import CardContent from '@app/components/card/card-content.tsx';
import Card from '@app/components/card/card.tsx';
import { OpeningTimeServiceType } from '@app/graphql/types/graphql.ts';
import { todayIs } from '@app/page/online-order/product-picker/utils/today';
import CDNLink from '@app/utils/cdn-link.ts';
import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { useTranslate } from '@tolgee/react';
import { useBranch } from '../logic/use-branch';

export default function ReservationBusinessCard(props: { branchID: string }) {
  const today = todayIs();
  const { t } = useTranslate();
  const { branch, loading, error } = useBranch(props.branchID);

  if (loading) {
    return (
      <Card className="z-10 shadow-md sm:relative">
        <CardContent>
          <div className="flex items-center justify-center p-4">
            <div className="text-gray-500">{t('common.loading', 'Loading...')}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !branch) {
    return (
      <Card className="z-10 shadow-md sm:relative">
        <CardContent>
          <div className="flex items-center justify-center p-4">
            <div className="text-red-500">{t('common.error', 'Error loading branch information')}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const todayOpeningTime = branch.openingTimes?.filter(
    (item) => item.dayOfWeek === today && item.service === OpeningTimeServiceType.TableReservation
  );

  return (
    <Card className="z-10 shadow-md">
      <CardContent>
        <div className="m-auto w-full max-w-5xl space-y-6">
          <div className="flex items-start justify-between gap-x-4">
            <div className="flex items-center gap-x-3">
              {branch.company.logo && (
                <img className="w-28 rounded-md" src={CDNLink(branch.company.logo)} alt={branch.company.name} />
              )}
              <div className="space-y-1.5">
                <p className="pt-2 text-xl font-semibold">{branch.company.name}</p>
                <p className="flex flex-wrap gap-x-1 text-sm text-gray-600">
                  <span>{branch.streetAddress},</span>
                  <span>{branch.postalCode}</span>
                  <span>
                    {branch.addressLocality}
                    {branch.addressCountry && <>,</>}{' '}
                  </span>
                  <span>{branch.addressCountry}</span>
                </p>
                {todayOpeningTime && todayOpeningTime.length > 0 && (
                  <div className="flex items-center gap-x-1 text-sm text-gray-500">
                    <span>{t('reservation.business-card.opening', 'Opening time')}:</span>
                    {todayOpeningTime.map((item, index) => (
                      <p key={index}>
                        {item.openTime} - {item.closeTime}
                        {index < todayOpeningTime.length - 1 && <span>,</span>}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            {branch.contactEmail && (
              <div className="inline-flex items-center gap-x-1 rounded-md border border-gray-300 p-2 text-sm text-gray-800">
                <a href={`mailto:${branch.contactEmail}`} className="hover:text-indigo-600">
                  {branch.contactEmail}
                </a>
                <EnvelopeIcon className="h-4 w-4" />
              </div>
            )}
            {branch.contactPhone && (
              <div className="inline-flex items-center gap-x-1 rounded-md bg-indigo-500 p-2 text-sm text-white">
                <a href={`tel:${branch.contactPhone}`} className="hover:text-indigo-600">
                  {branch.contactPhone}
                </a>
                <PhoneIcon className="h-4 w-4" />
              </div>
            )}

            {branch.website && (
              <a
                href={branch.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block inline-block rounded-md border border-2 p-2 text-sm text-indigo-600 hover:text-indigo-500"
              >
                {t('reservation.button.return-to-website', 'Return to website')}
              </a>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
